/**
 * Owner-facing Changes / Work Result surface.
 * Shows only facts Brunel actually recorded; missing patch/evidence stays Unknown.
 */
import { changesSnapshotFromSelection, type AutomaticHandoff } from '@brunel/shared';

export function ChangesView({ workerLabel, projectName, taskTitle, taskId, nodeName, sessionId,
  branch, filesChanged, patchText, runSummary, handoff }: {
  readonly workerLabel: string | null; readonly projectName: string | null;
  readonly taskTitle: string | null; readonly taskId: string | null;
  readonly nodeName: string | null; readonly sessionId: string | null;
  readonly branch: string | null; readonly filesChanged: readonly string[] | null;
  readonly patchText: string | null; readonly runSummary: string | null;
  readonly handoff: AutomaticHandoff | null;
}) {
  const snapshot = changesSnapshotFromSelection({ workerLabel, projectName, taskTitle, taskId, nodeName,
    sessionId, branch, filesChanged, patchText, runSummary, handoff });
  return (
    <div className="changes-view stack" aria-label="Changes and work result">
      <header className="stack tight"><h3>Changes / Work Result</h3><Meta snapshot={snapshot} /></header>
      <section className="changes-section"><h4>Files changed</h4>
        {snapshot.filesChanged == null ? <p className="muted">Unknown — changed files were not recorded for this selection.</p>
          : snapshot.filesChanged.length === 0 ? <p className="muted">No changed files were recorded.</p>
          : <ul className="changes-files">{snapshot.filesChanged.map((file) => <li key={file}><code>{file}</code></li>)}</ul>}
      </section>
      <section className="changes-section"><h4>Patch / diff</h4>
        {snapshot.patchText ? <pre className="changes-patch">{snapshot.patchText}</pre>
          : <p className="muted">Unknown — no patch/diff was recorded for this run.</p>}
      </section>
      <section className="changes-section"><h4>Tests / results</h4>
        {snapshot.tests == null ? <p className="muted">Unknown — test results were not recorded.</p>
          : snapshot.tests.length === 0 ? <p className="muted">No tests were reported.</p>
          : <ul className="changes-tests">{snapshot.tests.map((test) => (
              <li key={`${test.name ?? test.command ?? test.status}`}><span className={`test-status status-${test.status}`}>{test.status}</span>{' '}{test.name ?? test.command ?? 'test'}</li>
            ))}</ul>}
      </section>
      <section className="changes-section"><h4>Commit / PR</h4>
        <p className="muted">Commit: {snapshot.commitSha ?? 'Unknown — not recorded'}</p>
        <p className="muted">PR: {snapshot.pullRequest == null ? 'Unknown — not recorded'
          : (snapshot.pullRequest.url ?? (snapshot.pullRequest.number == null ? 'Recorded without a link/number' : `#${snapshot.pullRequest.number}`))}</p>
      </section>
      <section className="changes-section"><h4>Artifacts / evidence</h4>
        {snapshot.artifacts.length === 0 ? <p className="muted">Unknown — no artifacts/evidence were recorded.</p>
          : <ul className="changes-artifacts">{snapshot.artifacts.map((item) => <li key={item.reference}>{item.label}: {item.url ?? item.reference}</li>)}</ul>}
      </section>
      {snapshot.sessionId && <p className="muted">Session {snapshot.sessionId}</p>}
    </div>
  );
}
function Meta({ snapshot }: { readonly snapshot: ReturnType<typeof changesSnapshotFromSelection> }) {
  const bits=[snapshot.projectName,snapshot.taskTitle,snapshot.branch,snapshot.nodeName].filter((bit): bit is string => bit != null && bit !== '');
  if (bits.length===0 && snapshot.runSummary==null) return null;
  return <div className="changes-meta">{bits.length>0 && <p>{bits.join(' · ')}</p>}{snapshot.runSummary && <p className="muted">{snapshot.runSummary}</p>}</div>;
}
